import { useState } from "react";
import axios from "axios";
import { useMutation, useQuery } from "@tanstack/react-query";

export const useNavigation = () => {
  const navigateToUrl = (url, shouldReplace = false) => {
    if (shouldReplace) {
      history.replaceState({}, "", url);
    } else {
      history.pushState({}, "", url);
    }
    history.go();
  };

  const navigateBack = () => {
    history.back();
  };

  const getCurrentPath = () => window.location.pathname;

  const isOnPageWithPathPart = (pathPart) => {
    return window.location.pathname.includes(pathPart);
  };

  const reloadPage = () => {
    window.location.reload();
  };

  return {
    navigateToUrl,
    navigateBack,
    getCurrentPath,
    isOnPageWithPathPart,
    reloadPage,
  };
};

export const useDeleteDraft = (draftId) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const navigation = useNavigation();

  const deleteDraft = async () => {
    setLoading(true);
    setError(null);
    try {
      await axios.delete(`/api/datasets/${draftId}/draft`);
    } catch (err) {
      console.error("Error deleting draft:", err);
      setError(err);
    } finally {
      setLoading(false);
      if (navigation.isOnPageWithPathPart("preview")) {
        navigation.navigateToUrl("/me/records");
      } else {
        navigation.reloadPage();
      }
    }
  };

  return { deleteDraft, loading, error };
};

export const usePublishDraft = (draftId) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const navigation = useNavigation();

  const publishDraft = async () => {
    setLoading(true);
    setError(null);
    let publishRequestId = null;
    try {
      const requestInfo = await axios.post(
        `/api/datasets/${draftId}/draft/requests/publish_draft`,
      );
      publishRequestId = requestInfo.data.id;
      await axios.post(`/api/requests/${publishRequestId}/actions/submit`);
    } catch (err) {
      console.error("Error publishing draft:", err);
      setError(err);
    } finally {
      if (navigation.isOnPageWithPathPart("preview")) {
        navigation.navigateToUrl(`/requests/${publishRequestId}`);
      }
      setLoading(false);
    }
  };

  return { publishDraft, loading, error };
};

export { useSubmitReview } from "./useSubmitReview";

export const useRunExternalWorkflow = (draftId) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const runExternalWorkflow = async () => {
    setLoading(true);
    setError(null);
    try {
      const requestInfo = await axios.post(
        `/api/datasets/${draftId}/draft/requests/run_external_workflow`,
      );
      const requestId = requestInfo.data.id;
      await axios.post(`/api/requests/${requestId}/actions/submit`);
    } catch (err) {
      console.error("Error running external workflow:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  return { runExternalWorkflow, loading, error };
};

const useGetAllowedFeatures = () => {
  return useQuery({
    queryKey: ["features"],
    queryFn: async () => {
      const res = await axios.get(`/api/features`);
      return res.data;
    },
    staleTime: 1000 * 60 * 60, // 60 minute stale time, after which the query will be refetched
  });
};

export const useFeature = (featureName) => {
  const { data, isLoading } = useGetAllowedFeatures();
  return {
    isLoading,
    enabled: data?.includes(featureName) ?? undefined,
  };
};

export const useGetFromUrl = (url, options = {}) => {
  return useQuery({
    queryKey: [url],
    queryFn: async () => {
      const res = await axios.get(url);
      return res.data;
    },
    staleTime: 1000 * 60 * 60,
    ...options, // Allow additional query options to be passed
  });
};

export const useEditDraft = () => {
  return useMutation({
    mutationFn: async (draft) => {
      const draftId = draft.id;
      await axios.put(`/api/datasets/${draftId}/draft`, draft);
    },
    onError: () => {
      console.error("Error editing draft");
    },
  });
};

export const usePostRequestAction = (urlAction) => {
  return useMutation({
    mutationFn: async (urlAction) => {
      const requestResponse = await axios.post(urlAction);
      return requestResponse.data;
    },
    onError: () => {
      console.error("Error in post request action");
    },
  });
};

export const useCleanupAndCreateRequest = () => {
  return useMutation({
    mutationFn: async ({
      draftId,
      requestType,
      draftTitle,
      datasetTypeCode,
    }) => {
      const requestsRes = await axios.get(
        `/api/requests/?topic=record:${draftId}`,
      );
      const requests = requestsRes.data.hits.hits;

      const createdRequestsToCancel = requests.filter(
        (r) => r.type === requestType && r.status === "created",
      );
      await Promise.all(
        createdRequestsToCancel.map((r) => axios.post(r.links.actions.cancel)),
      );

      const response = await axios.post(
        `/api/datasets/${draftId}/draft/requests/${requestType}`,
        { title: `${datasetTypeCode} - ${draftTitle}` },
      );
      return response.data;
    },
    onError: (error) => {
      console.error(`Could not create request. Error: ${error}`);
    },
  });
};

export const useGetDraftRequestsWithRefetch = (draftId, inReview = false) => {
  return useQuery({
    queryKey: ["draftRequests", draftId],
    queryFn: async () => {
      const res = await axios.get(`/api/requests/?topic=record:${draftId}`);
      return res.data.hits.hits;
    },
    refetchInterval: inReview ? 15_000 : false,
  });
};

export const useGetDraftRequests = (draftId) => {
  return useQuery({
    queryKey: [draftId],
    queryFn: async () => {
      const res = await axios.get(`/api/requests/?topic=record:${draftId}`);
      return res.data;
    },
    onError: () => {
      console.error(
        `Could not fetch draft requests on draft with id ${draftId}`,
      );
    },
  });
};

export const useGetDraft = (draftId) => {
  return useQuery({
    queryKey: ["draft", draftId],
    queryFn: async () => {
      const res = await axios.get(`/api/datasets/${draftId}/draft`);
      return res.data;
    },
    enabled: !!draftId,
  });
};

export const useCreateDraft = (data) => {
  return useMutation({
    mutationFn: async (data) => {
      const res = await axios.post(`/api/datasets`, data);
      return res.data;
    },
    onError: () => {
      console.error("Error creating draft");
    },
  });
};

export const useGetMyRecords = (params) => {
  return useQuery({
    queryKey: ["myRecords", params],
    queryFn: async () => {
      const res = await axios.get(`/api/user/search`, { params });
      return res.data;
    },
    keepPreviousData: true,
  });
};

export const useGetUserCommunities = (page = 1, size = 10) => {
  return useQuery({
    queryKey: ["userCommunities", page, size],
    queryFn: async () => {
      const res = await axios.get(
        `/api/user/communities?q=&sort=newest&page=${page}&size=${size}`
      );
      return res.data;
    },
  });
};
